import { FormEvent, useState } from 'react';
import { Box, Input, Select, useToast } from '@chakra-ui/react';
import { adicionarBook } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { toBase64, uploadWithBase64 } from '../../utils/imageHelper';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
};

type Campo = 'nome' | 'descricao' | 'capa';

const IDIOMA_PADRAO = 'Inglês';
const NIVEL_PADRAO = 'Beginner';

export function DrawerCreate({ isOpen, onClose, afterClose }: DrawerCreateProps) {
	const toast = useToast();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ nome, setNome ] = useState('');
	const [ descricao, setDescricao ] = useState('');
	const [ capa, setCapa ] = useState('');
	const [ idioma, setIdioma ] = useState(IDIOMA_PADRAO);
	const [ nivel, setNivel ] = useState(NIVEL_PADRAO);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(nome)) encontrados.nome = 'Nome do book é obrigatório!';
		if (vazio(descricao)) encontrados.descricao = 'Descrição é obrigatória!';
		if (!capa) encontrados.capa = 'Capa é obrigatória!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		uploadWithBase64(capa)
			.then((link) =>
				adicionarBook({
					capa: link,
					descricao: descricao.trim(),
					idioma,
					nivel,
					nome: nome.trim()
				})
			)
			.then(() => {
				setLoading(false);
				toast({
					title: `Livro adicionado com sucesso!`,
					status: 'success',
					isClosable: true
				});
				resetForm();
				onClose();
				afterClose();
			})
			// Sem este catch uma falha de upload deixava o spinner girando para sempre.
			.catch((error) => {
				setLoading(false);
				const message = error?.response?.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível adicionar o livro.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	// Idioma e nivel voltam para o padrao, e nao para string vazia, senao o Select
	// reabre sem nenhuma opcao correspondente.
	const resetForm = () => {
		setCapa('');
		setDescricao('');
		setIdioma(IDIOMA_PADRAO);
		setNivel(NIVEL_PADRAO);
		setNome('');
		limparTudo();
	};

	const handleSelectedFile = async (files: FileList | null) => {
		if (files && files[0] && files[0].size < 10000000) {
			const picBase64 = await toBase64(files[0]);
			setCapa(`${picBase64}`);
			limparErro('capa');
		} else {
			toast({
				title: `Foto muito pesada, selecione outra!`,
				status: 'warning',
				isClosable: true
			});
		}
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<DrawerForm
			isOpen={isOpen}
			onClose={handleFechar}
			titulo="Novo Book"
			formId="form-novo-book"
			loading={loading}
			loadingLabel="Salvando..."
			onSubmit={handleSalvar}
		>
			<CampoForm label="Nome do Book" htmlFor="nome" erro={erros.nome}>
				<Input
					id="nome"
					placeholder="Insira o Nome do book"
					onChange={(e) => {
						setNome(e.target.value);
						limparErro('nome');
					}}
					value={nome}
				/>
			</CampoForm>

			<CampoForm label="Descrição" htmlFor="descricao" erro={erros.descricao}>
				<Input
					id="descricao"
					placeholder="Insira a Descrição"
					onChange={(e) => {
						setDescricao(e.target.value);
						limparErro('descricao');
					}}
					value={descricao}
				/>
			</CampoForm>

			<CampoForm label="Idioma" htmlFor="idioma">
				<Select id="idioma" onChange={(e) => setIdioma(e.target.value)} value={idioma}>
					<option value="Inglês">Inglês</option>
					<option value="Português">Português</option>
					<option value="Italiano">Italiano</option>
					<option value="Espanhol">Espanhol</option>
					<option value="Francês">Francês</option>
				</Select>
			</CampoForm>

			<CampoForm label="Nível" htmlFor="nivel">
				<Select id="nivel" onChange={(e) => setNivel(e.target.value)} value={nivel}>
					<option value="Beginner">Beginner</option>
					<option value="Intermediate">Intermediate</option>
					<option value="Advanced">Advanced</option>
				</Select>
			</CampoForm>

			<Box
				width="100%"
				height="150px"
				borderWidth="1px"
				borderColor="gray.200"
				borderRadius="10px"
				bgPosition="center"
				bgSize="cover"
				bgImage={capa}
			/>

			<CampoForm label="Capa" htmlFor="capa" erro={erros.capa}>
				<Input
					id="capa"
					type="file"
					accept="image/*"
					paddingTop="4px"
					onChange={(files) => handleSelectedFile(files.target.files)}
				/>
			</CampoForm>
		</DrawerForm>
	);
}
